import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useApolloClient } from '@apollo/client';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { FileType } from '@models/file';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';
import { resolveGraphqlBaseUrl } from '@utils/tenant-utils';
import { getUserToken } from '@utils/userUtils';

import { DocumentNode, print } from 'graphql';
import { cloneDeep } from 'lodash';
import { IBaseDrawerProps } from '@common/types';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

const drawerSections: IDynamicFormWidgetSection[] = [
  {
    inputs: {
      documentName: {
        name: 'documentName',
        title: 'Document Name',
        type: FormFieldType.text,
        placeholder: 'Enter document name',
        value: '',
        required: true
      },
      document: {
        name: 'document',
        title: 'Document',
        type: FormFieldType.fileUploader,
        value: null,
        required: true,
        disabled: false,
        placeholder: 'Upload Document',
        maxFileSizeInMB: 5,
        allowedFileTypes: [
          FileType.pdf,
          FileType.docx,
          FileType.jpeg,
          FileType.png
        ]
      }
    }
  }
];
interface Props extends IBaseDrawerProps {
  addDocumentMutation: DocumentNode;
  addDocumentVariables: Record<string, any>;
  refetchQueries?: DocumentNode[];
};

const AddDocumentDrawer: React.FC<Props> = ({
  onSuccess,
  addDocumentMutation,
  addDocumentVariables,
  refetchQueries
}) => {
  const client = useApolloClient();
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = () => {
    try {
      const updatedInputs = cloneDeep(inputsForm);
      setInputsForm(updatedInputs);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const simulateFileUpload = async (
    documentName: string,
    file: File
  ): Promise<void> => {
    const allowedExtensions = ['pdf', 'docx', 'jpeg', 'jpg', 'png'];

    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      throw new Error(
        'Invalid file format. Please upload a PDF or DOCX or JPEG or PNG file.'
      );
    }

    const formData = new FormData();

    formData.append('formFile', file, file.name);

    formData.append(
      'operations',
      JSON.stringify({
        query: print(addDocumentMutation),
        variables: {
          ...addDocumentVariables,
          documentName: documentName,
          document: null
        }
      })
    );
    formData.append(
      'map',
      JSON.stringify({ formFile: ['variables.document'] })
    );

    const options = {
      method: 'POST',
      headers: {
        authorization: `Bearer ${getUserToken()}` || null,
        'GraphQL-preflight': '1'
      },
      body: formData
    };

    try {
      const response = await fetch(
        `${resolveGraphqlBaseUrl()}/graphql`,
        options
      );

      if (response.ok) {
        // Refetch queries if provided
        if (refetchQueries && refetchQueries.length > 0) {
          await client.refetchQueries({
            include: refetchQueries
          });
        }

        toast.success(
          <ToastSuccessMessage>
            {'Document successfully uploaded'}
          </ToastSuccessMessage>
        );
      } else {
        throw new Error(getError(response));
      }
    } catch (error) {
      throw new Error(DEFAULT_ERROR_TEXT);
    }
  };

  const onSubmit = async (values: Record<string, any>) => {
    try {
      await simulateFileUpload(values.documentName, values.document);
      onSuccess();
    } catch (error) {
      if (error.message.includes('Invalid file format')) {
        toast.error(<ToastErrorMessage>{error.message}</ToastErrorMessage>);
      } else {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    }
  };

  return (
    <DynamicFormWidget
      sections={inputsForm}
      onSubmit={onSubmit}
      submitButtonState={submitButtonState}
    />
  );
};

export { AddDocumentDrawer };
